/**
 * LazyImage Component
 * 
 * A performance-optimized image component that:
 * - Automatically converts .jpg/.png paths to .webp (WebP-only mode)
 * - Implements native lazy loading
 * - Improves LCP (Largest Contentful Paint) and SEO
 * 
 * IMPORTANT: After WebP migration, all images are served as .webp only.
 * This component automatically transforms any legacy .jpg/.png paths to .webp
 * 
 * Usage:
 *   <LazyImage
 *     src="/images/upload/x_medium_abc123.jpeg"  // Auto-converted to .webp
 *     alt="Boat description"
 *     className={s.cardImage}
 *   />
 */

import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './LazyImage.css';

class LazyImage extends React.Component {
  static propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    className: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sizes: PropTypes.string,
    placeholder: PropTypes.string,
    onLoad: PropTypes.func,
    onError: PropTypes.func,
    priority: PropTypes.bool, // For above-the-fold images (no lazy loading)
  };

  static defaultProps = {
    className: '',
    width: undefined,
    height: undefined,
    sizes: undefined,
    placeholder: undefined,
    onLoad: undefined,
    onError: undefined,
    priority: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      hasError: false,
    };
  }

  /**
   * Convert any image path to WebP path
   * This is the PRIMARY source now - all images are WebP only after migration
   * @param {string} src - Original image source (may be .jpg, .jpeg, .png, or already .webp)
   * @returns {string} WebP image source
   */
  toWebp = (src) => {
    if (!src) return '';
    // If already .webp, return as-is
    if (/\.webp$/i.test(src)) return src;
    // Convert .jpeg, .jpg, .png to .webp
    return src.replace(/\.(jpe?g|png)$/i, '.webp');
  };

  /**
   * Legacy method - kept for compatibility but now just calls toWebp
   * @deprecated Use toWebp instead
   */
  getWebpSrc = (src) => {
    return this.toWebp(src);
  };

  /**
   * Generate srcset for responsive images
   * @param {string} src - Base image source
   * @param {boolean} isWebp - Whether to generate WebP srcset
   * @returns {string} srcset string
   */
  getSrcSet = (src, isWebp = false) => {
    if (!src) return '';
    
    const basePath = src.replace(/\.(jpe?g|png|webp)$/i, '');
    const ext = isWebp ? '.webp' : src.match(/\.(jpe?g|png)$/i)?.[0] || '.jpeg';
    
    // Extract prefix and filename for different sizes
    const match = src.match(/(.+\/)(x_medium_|x_large_|xx_large_|small_|x_small_)?(.+)$/);
    if (!match) return '';
    
    const [, path, , filename] = match;
    const baseFilename = filename.replace(/\.(jpe?g|png|webp)$/i, '');
    const originalExt = isWebp ? '.webp' : (filename.match(/\.(jpe?g|png)$/i)?.[0] || '.jpeg');
    
    // Generate srcset with different sizes
    return [
      `${path}x_small_${baseFilename}${originalExt} 216w`,
      `${path}small_${baseFilename}${originalExt} 400w`,
      `${path}x_medium_${baseFilename}${originalExt} 640w`,
      `${path}x_large_${baseFilename}${originalExt} 900w`,
      `${path}xx_large_${baseFilename}${originalExt} 1280w`,
    ].join(', ');
  };

  handleLoad = (e) => {
    this.setState({ isLoaded: true });
    if (this.props.onLoad) {
      this.props.onLoad(e);
    }
  };

  handleError = (e) => {
    this.setState({ hasError: true });
    if (this.props.onError) {
      this.props.onError(e);
    }
  };

  render() {
    const { 
      src, 
      alt, 
      className, 
      width, 
      height, 
      sizes,
      placeholder,
      priority,
    } = this.props;
    const { isLoaded, hasError } = this.state;

    if (!src) {
      return null;
    }

    // IMPORTANT: All images are now WebP-only after migration
    // Automatically convert any .jpg/.png path to .webp
    const webpSrc = this.toWebp(src);
    const loadingAttr = priority ? 'eager' : 'lazy';
    const fetchPriorityAttr = priority ? 'high' : 'auto';

    // Container classes
    const containerClass = [
      s.lazyImageContainer,
      isLoaded ? s.loaded : '',
      className,
    ].filter(Boolean).join(' ');

    return (
      <div className={containerClass}>
        {/* WebP-only image with lazy loading */}
        <img
          src={webpSrc}
          alt={alt}
          width={width}
          height={height}
          loading={loadingAttr}
          decoding="async"
          fetchpriority={fetchPriorityAttr}
          onLoad={this.handleLoad}
          onError={this.handleError}
          className={s.lazyImage}
          style={hasError ? { display: 'none' } : undefined}
        />
        
        {/* Show placeholder on error */}
        {hasError && placeholder && (
          <img
            src={this.toWebp(placeholder)}
            alt={alt}
            className={s.lazyImage}
          />
        )}
      </div>
    );
  }
}

export default withStyles(s)(LazyImage);
